import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PlugsConnected,
  CheckCircle,
  ArrowSquareOut,
  Plugs,
  ShieldCheck,
  Info,
} from "@phosphor-icons/react";
import { useAppServices } from "../../../hook/services";
import { useAppInfo } from "../../../context/app";
import LoadingScreen from "../../../components/LoadingScreen";
import { SERVICEM8_OAUTH_CONTEXT_KEY } from "../../integration/servicem8";

const formatDate = (value) => {
  if (!value) return "—";
  const date = typeof value === "number" ? new Date(value) : new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const Connect = () => {
  const { appId, locationId, companyId } = useParams();
  const AppService = useAppServices();
  const [app] = useAppInfo();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connection, setConnection] = useState({ connected: false });

  const loadStatus = async () => {
    const { response } = await AppService.servicem8.connection_status({
      appId,
      query: `locationId=${locationId}&companyId=${companyId}`,
    });
    if (response?.success) setConnection(response.data || { connected: false });
    return response?.data;
  };

  useEffect(() => {
    const load = async () => {
      await loadStatus();
      setLoading(false);
    };
    load();
  }, []);

  // The popup posts back here the moment ServiceM8 redirects and the code is
  // exchanged, so the page can flip to "connected" without a manual refresh.
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.data?.message !== "SERVICEM8_OAUTH_RESULT") return;

      setConnecting(false);

      if (event.data.success) {
        toast.success("ServiceM8 connected!");
        await loadStatus();
      } else {
        toast.error(event.data.error || "ServiceM8 connection failed.");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const handleConnect = async () => {
    setConnecting(true);

    const { response, error } = await AppService.servicem8.get_auth_url({
      appId,
      payload: { extras: { locationId, companyId } },
    });

    if (!response?.success) {
      setConnecting(false);
      return toast.error(
        error?.message || "Could not start the ServiceM8 connection."
      );
    }

    // Fallback for the callback page in case ServiceM8 drops `state`.
    window.localStorage.setItem(
      SERVICEM8_OAUTH_CONTEXT_KEY,
      JSON.stringify({ appId, locationId, companyId })
    );

    const popup = window.open(
      response.data.url,
      "servicem8-oauth",
      "width=620,height=760"
    );

    // Popup blocked (common inside the CRM iframe) — hand them the URL instead
    // of leaving the button spinning forever.
    if (!popup) {
      setConnecting(false);
      window.open(response.data.url, "_blank", "noopener");
    }
  };

  const handleTest = async () => {
    setTesting(true);
    const { response, error } = await AppService.servicem8.test_connection({
      appId,
      query: `locationId=${locationId}&companyId=${companyId}`,
    });
    setTesting(false);

    if (response?.success) {
      toast.success(response.message || "ServiceM8 connection is working!");
      await loadStatus();
    } else {
      toast.error(error?.message || "ServiceM8 connection test failed.");
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    const { response, error } = await AppService.servicem8.disconnect({
      appId,
      payload: { extras: { locationId, companyId } },
    });
    setDisconnecting(false);

    if (response?.success) {
      setConnection({ connected: false });
      toast.success("ServiceM8 disconnected.");
    } else {
      toast.error(error?.message || "Could not disconnect ServiceM8.");
    }
  };

  if (loading) return <LoadingScreen loadingText={"Loading data..."} />;

  const scopes = (connection.scope || "").split(/\s+/).filter(Boolean);

  return (
    <div className="py-5 bg-light min-vh-100">
      <div className="container" style={{ maxWidth: 860 }}>
        {/* Header */}
        <div className="card border-0 shadow-sm mb-4 rounded-4">
          <div className="card-body d-flex align-items-center p-4">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
              style={{
                width: 55,
                height: 55,
                background: "linear-gradient(135deg, #f97316, #ea580c)",
                color: "white",
              }}
            >
              <PlugsConnected size={24} weight="bold" />
            </div>
            <div>
              <h4 className="fw-bold mb-1">
                {app?.appName || "ServiceM8"} Connection
              </h4>
              <p className="mb-0 text-muted small">
                Authorize this location's ServiceM8 account so jobs and
                customers can sync.
              </p>
            </div>
          </div>
        </div>

        {/* Connection card */}
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body p-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
              <div className="d-flex align-items-center gap-2">
                {connection.connected ? (
                  <>
                    <CheckCircle
                      size={22}
                      weight="fill"
                      className="text-success"
                    />
                    <span className="fw-semibold text-success">
                      Connected to ServiceM8
                    </span>
                  </>
                ) : (
                  <>
                    <Plugs size={22} weight="bold" className="text-secondary" />
                    <span className="fw-semibold text-secondary">
                      Not connected
                    </span>
                  </>
                )}
                {connection.expired && (
                  <span className="badge bg-warning-subtle text-warning-emphasis ms-2">
                    Token expired — will auto-refresh
                  </span>
                )}
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleConnect}
                  disabled={connecting}
                  className="btn btn-primary fw-semibold rounded-3 d-flex align-items-center gap-2"
                >
                  <ArrowSquareOut size={16} weight="bold" />
                  {connecting
                    ? "Waiting for ServiceM8…"
                    : connection.connected
                    ? "Reconnect"
                    : "Connect ServiceM8"}
                </button>

                {connection.connected && (
                  <>
                    <button
                      type="button"
                      onClick={handleTest}
                      disabled={testing}
                      className="btn btn-outline-secondary fw-semibold rounded-3"
                    >
                      {testing ? "Testing…" : "Test Connection"}
                    </button>
                    <button
                      type="button"
                      onClick={handleDisconnect}
                      disabled={disconnecting}
                      className="btn btn-outline-danger fw-semibold rounded-3"
                    >
                      {disconnecting ? "Disconnecting…" : "Disconnect"}
                    </button>
                  </>
                )}
              </div>
            </div>

            {connection.connected && (
              <div className="row g-3 mb-1">
                <div className="col-sm-6">
                  <div className="border rounded-3 p-3 h-100">
                    <div className="text-muted small mb-1">Connected on</div>
                    <div className="fw-medium small">
                      {formatDate(connection.connected_at)}
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="border rounded-3 p-3 h-100">
                    <div className="text-muted small mb-1">
                      Access token expires
                    </div>
                    <div className="fw-medium small">
                      {formatDate(connection.expires_at)}
                    </div>
                  </div>
                </div>

                {scopes.length > 0 && (
                  <div className="col-12">
                    <div className="border rounded-3 p-3">
                      <div className="text-muted small mb-2 d-flex align-items-center gap-2">
                        <ShieldCheck size={15} weight="bold" />
                        Granted permissions
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {scopes.map((scope) => (
                          <span
                            key={scope}
                            className="badge bg-light text-dark border fw-normal"
                          >
                            {scope}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!connection.connected && (
              <div className="small p-3 border rounded-3 bg-light text-muted d-flex align-items-start gap-2">
                <Info size={15} className="text-info flex-shrink-0 mt-1" />
                <span>
                  Clicking Connect opens ServiceM8 in a new window where you
                  sign in and approve access. Tokens are stored on the server
                  and refreshed automatically — they're never exposed to the
                  browser.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Connect;
